import { Button, Form, Input, Modal, Select, Switch } from "antd";
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { createTrack, editTrack } from "../../redux/actions/trackActions";
import { RootState } from "../../redux/rootReducer";
import { Actions } from "../../shared/constant";
import validateMessages from "../../utils/validate";

interface ModalFormProps {
  visible: boolean;
  onCancel: () => void;
  callEffect: () => void;
  mode: string;
  editData: any;
}

const TrackModalForm = ({
  visible,
  onCancel,
  callEffect,
  mode,
  editData,
}: ModalFormProps) => {
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const [isActive, setIsActive] = useState(true);
  const { allLabels } = useSelector((state: RootState) => state.label);
  const { allArtists, allLyricists, allComposers } = useSelector(
    (state: RootState) => state.contributor,
  );
  const { pending, fulfilled } = useSelector((state: RootState) => state.track);

  useEffect(() => {
    // console.log("useEffect editData: ", mode, editData);
    if (mode === Actions.EDIT) {
      // if (editData.status === Status.INACTIVE) setIsActive(false);
      form.setFieldsValue({
        title: editData.title,
        labels: editData.label_id,
        ...(editData.artist && { artists: editData.artist?.split(", ") }),
        ...(editData.lyricists && {
          lyricists: editData.lyricists?.split(", "),
        }),
        ...(editData.composers && {
          composers: editData.composers?.split(", "),
        }),
        upc: editData.upc,
        isrc: editData.isrc,
        link: editData.link,
        // status: editData.status === Status.ACTIVE,
      });
    }
  }, [editData]);

  useEffect(() => {
    /* Create and Edit track success */
    if (fulfilled) {
      onCancel();
      callEffect();
    }
  }, [pending]);

  const handleSwitchToggle = (checked: boolean) => {
    setIsActive(checked);
  };

  const handleCreate = async () => {
    try {
      await form.validateFields();
    } catch (errorInfo) {
      return;
    }

    const fields = form.getFieldsValue(true);
    // console.log("fields: ", fields);

    const payload = {
      title: fields.title,
      labels: fields.labels,
      artist: fields.artists.join(", "),
      lyricists: fields.lyricists.join(", "),
      composers: fields.composers.join(", "),
      upc: fields.upc,
      isrc: fields.isrc,
      link: fields.link,
      status: fields.status,
    };

    const editPayload = {
      title: fields.title,
      labels: fields.labels,
      artist: fields.artists?.join(", "),
      lyricists: fields.lyricists?.join(", "),
      composers: fields.composers?.join(", "),
      link: fields.link,
    };

    if (mode === Actions.CREATE) dispatch(createTrack(payload));
    if (mode === Actions.EDIT)
      dispatch(editTrack({ id: editData?.id, body: editPayload }));
  };

  const afterClose = () => {
    form.resetFields();
  };

  const getSelectFields = (name: string, options: any) => {
    return (
      <div>
        <Form.Item
          name={`${name.toLowerCase()}s`}
          label={name}
          className="mb-[6px]"
          initialValue={[]}
        >
          <Select
            mode="multiple"
            placeholder="Please select"
            className="w-full"
            allowClear
          >
            {options.map((option: any) => (
              <Select.Option key={option.id} value={option.name}>
                {option.name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
      </div>
    );
  };

  return (
    <Modal
      visible={visible}
      title={`${mode === Actions.CREATE ? "Create New" : "Edit"} Track`}
      centered
      // mask={false}
      // maskClosable={false}
      // transitionName=""
      width={740}
      onOk={handleCreate}
      onCancel={onCancel}
      afterClose={afterClose}
      footer={[
        <Button key="back" onClick={onCancel}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={pending as boolean}
          onClick={handleCreate}
        >
          Save
        </Button>,
      ]}
    >
      <div className="mx-6 mt-6 mb-[26px]">
        <Form
          form={form}
          layout="vertical"
          name="trackForm"
          validateMessages={validateMessages}
        >
          <Form.Item
            name="title"
            label="Track Title"
            rules={[{ required: true }]}
          >
            <Input />
          </Form.Item>
          <div className="grid grid-cols-2 gap-6">
            <Form.Item name="labels" label="Label" rules={[{ required: true }]}>
              <Select
                placeholder="Please select"
                filterOption={(inputValue, option) =>
                  option?.props.children
                    .toString()
                    .toLowerCase()
                    .includes(inputValue.toLowerCase())
                }
                showSearch
                allowClear
              >
                {allLabels.map((label: any) => (
                  <Select.Option key={label.id} value={label.id}>
                    {label.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
            {getSelectFields("Artist", allArtists)}
          </div>
          <div className="grid grid-cols-2 gap-6">
            {getSelectFields("Lyricist", allLyricists)}
            {getSelectFields("Composer", allComposers)}
          </div>
          <div className="grid grid-cols-2 gap-6">
            <Form.Item name="upc" label="UPC">
              <Input
                placeholder="Enter UPC number"
                disabled={mode === Actions.EDIT}
              />
            </Form.Item>
            <Form.Item name="isrc" label="ISRC">
              <Input
                placeholder="Enter ISRC number"
                disabled={mode === Actions.EDIT}
              />
            </Form.Item>
          </div>
          <Form.Item name="link" label="Link">
            <Input />
          </Form.Item>
          <div className="flex gap-[26px] items-center mt-6">
            <div>
              <Form.Item
                valuePropName="checked"
                name="status"
                className="h-4"
                initialValue
              >
                <Switch defaultChecked onChange={handleSwitchToggle} />
              </Form.Item>
            </div>
            <div className="h-5">{isActive ? "Active" : "Inactive"}</div>
          </div>
        </Form>
      </div>
    </Modal>
  );
};

export default TrackModalForm;
